// What a chat tool changed, as the query keys of the pages that show it: when a tool of a turn ends
// done, the chat panel invalidates them and the page behind the chat reloads in place. Pure, so it
// is unit-tested; the tool names are the assistant's (apps/assistant/src/tools).
import type { QueryKey } from '@tanstack/react-query';
import { PROJECT_ROUTES } from '../../../app/routes.ts';
import { agentRunsKey, boardKey, designKey, designScope } from '../../platform/index.ts';

/** The design resources each write tool changes (keys under ['design', slug, branch, ...]). */
const DESIGN_EFFECTS: Record<string, readonly string[]> = {
  create_data_element: ['elements', 'element', 'dictionary'],
  update_data_element: ['elements', 'element', 'dictionary'],
  create_processing_rule: ['processing-rules', 'rule-usage', 'rule-test-cases'],
  author_processing_rule: ['processing-rules', 'rule-usage', 'rule-test-cases'],
  create_reference_list: ['reference-lists'],
  upsert_transformation: ['pipeline', 'strategies', 'structure'],
  run_pipeline: ['pipeline', 'runs'],
  save_report: ['reports'],
};

/** Board writes. */
const BOARD_EFFECTS = new Set(['file_board_task', 'comment_board_task']);

/** A delegation to a project agent adds a run to that agent's ledger (its tools only read). */
const AGENT_RUN_EFFECTS = new Set(['delegate_to_project_agent']);

/** Tools that only read (their end changes nothing on screen). */
const WRITE_VERB = /^(create|update|delete|add|remove|author|upsert|save|run|set|bind|attach|detach|import)_/;

/** The keys a tool that ended `done` makes stale; an unknown write tool makes the whole branch stale. */
export function keysChangedBy(tool: string, slug: string, branch: string): QueryKey[] {
  if (BOARD_EFFECTS.has(tool)) return [boardKey()];
  if (AGENT_RUN_EFFECTS.has(tool)) return [agentRunsKey([slug])];
  const design = DESIGN_EFFECTS[tool];
  if (design) return design.map((r) => designKey(slug, branch, r));
  if (WRITE_VERB.test(tool)) return [designScope(slug, branch)];
  return [];
}

/** The first segment of every project page the router knows. */
const PAGES = new Set(PROJECT_ROUTES.map((r) => r.path.split('/')[0]!));

/**
 * The project page a chat `navigate` block may open (`data-sources/3`, `board?card=12`), or null for
 * anything else (another origin, an unknown page, odd characters): checked here again, whatever the
 * assistant sent.
 */
export function chatPagePath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const [path = '', query = ''] = raw.trim().replace(/^\/+/, '').split('?', 2);
  const segments = path.split('/').filter(Boolean);
  if (!segments.length || !PAGES.has(segments[0]!)) return null;
  if (segments.length > 4 || !segments.every((s) => /^[A-Za-z0-9_:-][A-Za-z0-9_.:-]{0,119}$/.test(s)))
    return null;
  if (!/^[A-Za-z0-9_=&%.,:-]{0,400}$/.test(query)) return null;
  return `${segments.join('/')}${query ? `?${query}` : ''}`;
}

/** The chat SDK's window events (opening the chat with a draft, or a harness window). */
export { OPEN_CHAT_EVENT, OPEN_HARNESS_EVENT, openChatEvent } from '@cortex/sdk-chat-react';
